import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Input, inputType } from 'components/input/input';
import { Button, buttonType } from 'components/button/button';
import './login-with-password.scss';
import { normalizePhoneNumber } from 'core/utils/decimalExtentions';
import { postGeneric, responseValidator } from 'apis/api';
import { API } from 'apis';
import { useNavigate } from 'react-router-dom';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { fetchAndStoreMenuItems, persistSession } from 'core/utils/auth-session';

interface Props {}

export interface LoginResult {
    accessToken: string;
    expiresIn: number;
    isAuthorized: boolean;
    isTwoFactorEnabled: boolean;
    refreshToken: string;
    roles: string[];
    tokenType: string;
}

/** ۱۱ رقم و شروع با صفر — همان قاعده phoneRegex پروژه، بدون فلگ سراسری. */
const VALID_PHONE = /^0\d{10}$/;

export const LoginWithPassword: React.FC<Props> = () => {
    // شماره قبلی را پیش از پاک‌سازی localStorage می‌خوانیم تا فرم از پیش پر شود.
    const [phoneNumber, setPhoneNumber] = useState<string>(() => localStorage.getItem('phone') || '');
    const [password, setPassword] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [touched, setTouched] = useState<{ phone?: boolean; password?: boolean }>({});

    const navigate = useNavigate();

    useEffect(() => {
        localStorage.clear();
    }, []);

    const isPhoneValid = VALID_PHONE.test(phoneNumber);
    const canSubmit = isPhoneValid && password.length > 0 && !loading;

    const phoneError = touched.phone && !isPhoneValid ? 'شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود.' : undefined;
    const passwordError = touched.password && !password ? 'رمز عبور را وارد کنید.' : undefined;

    const onPhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        // ارقام فارسی/عربی، فاصله، خط تیره و پیش‌شماره +۹۸ همگی نرمال می‌شوند.
        setPhoneNumber(normalizePhoneNumber(e.target.value));
    }, []);

    const login = useCallback(() => {
        if (!VALID_PHONE.test(phoneNumber) || !password) {
            setTouched({ phone: true, password: true });
            return;
        }

        setLoading(true);
        postGeneric<LoginResult>(API.general.verifyCode, { phoneNumber, password })
            .then(async (res) => {
                if (!responseValidator(res.status) || !res.value) {
                    setLoading(false);
                    return;
                }

                if (res.value.isTwoFactorEnabled === true) {
                    setLoading(false);
                    localStorage.setItem('phone', phoneNumber);
                    navigate('/signin/code');
                    return;
                }

                persistSession(res.value);
                await fetchAndStoreMenuItems();
                setLoading(false);
                navigate('/dashboard/users');
            })
            .catch(() => {
                setLoading(false);
                navigate('/signin');
            });
    }, [phoneNumber, password, navigate]);

    const onSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            login();
        },
        [login],
    );

    return (
        <form className="login-with-password" onSubmit={onSubmit} noValidate>
            <div className="title">
                <h1>ورود به پنل مدیریت</h1>
                <p className="subtitle">برای ادامه، شماره موبایل و رمز عبور خود را وارد کنید.</p>
            </div>

            <Input
                label="شماره موبایل"
                placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                value={phoneNumber}
                onChange={onPhoneChange}
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                htmlType="tel"
                inputMode="numeric"
                autoComplete="username"
                name="phoneNumber"
                dir="ltr"
                maxLength={11}
                autoFocus
                error={phoneError}
                onPressEnter={login}
            />

            <Input
                label="رمز عبور"
                placeholder="رمز عبور خود را وارد کنید"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                htmlType={showPassword ? 'text' : 'password'}
                type={inputType.text}
                autoComplete="current-password"
                name="password"
                error={passwordError}
                onPressEnter={login}
                suffix={
                    <button
                        type="button"
                        className="toggle-password"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
                        aria-pressed={showPassword}
                        tabIndex={-1}
                    >
                        {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    </button>
                }
            />

            <Button
                disabled={!canSubmit}
                htmlType="submit"
                type={buttonType.info}
                text="ورود"
                loading={loading}
            />
        </form>
    );
};
